import { Controller } from '@hotwired/stimulus';
import {
  lockBodyScroll,
  trapFocus,
  releaseFocusTrap,
  unlockBodyScroll,
  unlockBackground,
} from '../../utils/FocusTrap';
import { clearVpePersistedChatErrorOnModalClose } from '../../utils/case_experience/vpe_chat_error_persistence';

const DEFAULT_CONTINUE_BUTTON_SELECTOR = '.aquifer-modal__actions .btn-primary';

export default class extends Controller {
  static values = {
    variant: { type: String, default: 'chat' },
    dynamic: { type: Boolean, default: false },
    continueButtonId: String,
  };

  connect() {
    this._continueHandled = false;
    this._onEscape = this._handleEscape.bind(this);
  }

  disconnect() {
    this._unbindEscape();
    if (this.isOpen) this._cleanupOnClose();
  }

  open() {
    if (!this.element.isConnected) return false;

    this._continueHandled = false;
    this._disableCloseWithEsc();

    const railsui = this.railsuiModalController;
    if (railsui && !railsui.isOpen) {
      railsui.show();
    } else if (!this.isOpen) {
      this._manualOpen();
    }

    this._bindEscape();
    return true;
  }

  close() {
    this._unbindEscape();

    const railsui = this.railsuiModalController;
    if (railsui?.isOpen) {
      railsui.close();
    } else if (this.isOpen) {
      this._manualClose();
    }

    this._cleanupOnClose();
    this._continueHandled = false;
  }

  requestContinue(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (this._continueHandled) return;

    this._continueHandled = true;
    this._disableContinueButtons(event);
    this._unbindEscape();

    this.dispatch('continue', {
      detail: { originalEvent: event },
      cancelable: true,
    });
  }

  reenableContinue() {
    this._continueHandled = false;
    this._enableContinueButtons();
    if (this.isOpen) this._bindEscape();
  }

  get isOpen() {
    const container = this.element.querySelector('[data-railsui-modal-target="container"]');
    return Boolean(container && !container.classList.contains('hidden'));
  }

  get railsuiModalController() {
    return this.application.getControllerForElementAndIdentifier(this.element, 'railsui-modal');
  }

  _handleEscape(event) {
    if (event.key !== 'Escape' || !this.element.isConnected || !this.isOpen) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.requestContinue(event);
  }

  _bindEscape() {
    this._unbindEscape();
    window.addEventListener('keyup', this._onEscape, true);
  }

  _unbindEscape() {
    window.removeEventListener('keyup', this._onEscape, true);
  }

  _disableCloseWithEsc() {
    const container = this.element.querySelector('[data-railsui-modal-target="container"]');
    if (!container) return;

    const action = container.getAttribute('data-action') || '';
    const filtered = action
      .split(/\s+/)
      .filter((entry) => entry && !entry.includes('closeWithEsc'))
      .join(' ')
      .trim();

    if (filtered) container.setAttribute('data-action', filtered);
    else container.removeAttribute('data-action');
  }

  _manualOpen() {
    const container = this.element.querySelector('[data-railsui-modal-target="container"]');
    const content = this.element.querySelector('[data-railsui-modal-target="content"]');
    if (!container || !content) return false;

    if (this.element.dataset.railsuiModalWideValue === 'true') {
      container.classList.add('bg-black/50');
    } else {
      container.classList.add('aquifer-modal-overlay');
    }
    container.classList.remove('hidden');
    container.setAttribute('aria-hidden', 'false');
    content.classList.remove('hidden');

    lockBodyScroll();
    trapFocus(container, { skipInitialFocus: true });

    const focusSelector = this._continueFocusSelector();
    if (focusSelector) this.element.querySelector(focusSelector)?.focus();

    return true;
  }

  _manualClose() {
    const container = this.element.querySelector('[data-railsui-modal-target="container"]');
    const content = this.element.querySelector('[data-railsui-modal-target="content"]');
    if (!container || !content) return false;

    container.classList.add('hidden');
    container.classList.remove('bg-black/50', 'aquifer-modal-overlay');
    container.setAttribute('aria-hidden', 'true');
    releaseFocusTrap(container, { restoreFocus: false });
    content.classList.add('hidden');
    unlockBackground(this.element);
    unlockBodyScroll();
    document.body.style.overflow = '';
    document.body.classList.remove('overflow-hidden');
    return true;
  }

  _cleanupOnClose() {
    clearVpePersistedChatErrorOnModalClose(this.element);
    this._releaseBodyScrollIfNoModalRemains();
  }

  _releaseBodyScrollIfNoModalRemains() {
    const anotherModalOpen = Array.from(
      document.querySelectorAll('[data-railsui-modal-target="container"]'),
    ).some((container) => !container.classList.contains('hidden'));

    if (!anotherModalOpen) {
      document.body.style.overflow = '';
      document.body.classList.remove('overflow-hidden');
    }
  }

  _continueFocusSelector() {
    if (this.hasContinueButtonIdValue) return `#${this.continueButtonIdValue}`;

    const railsuiSelector = this.element.dataset.railsuiModalInitialFocusSelectorValue;
    if (railsuiSelector) return railsuiSelector;

    return null;
  }

  _resolveContinueButtons(event) {
    const buttons = new Set();

    if (event?.currentTarget instanceof HTMLElement) {
      buttons.add(event.currentTarget);
    }

    const continueButtonId = this.hasContinueButtonIdValue
      ? this.continueButtonIdValue
      : this._continueButtonIdFromDom();
    if (continueButtonId) {
      const scoped = this.element.querySelector(`#${continueButtonId}`);
      if (scoped) buttons.add(scoped);
    }

    const primary = this.element.querySelector(DEFAULT_CONTINUE_BUTTON_SELECTOR);
    if (primary) buttons.add(primary);

    const closeButton = this.element.querySelector('.aquifer-modal__close');
    if (closeButton) buttons.add(closeButton);

    return Array.from(buttons);
  }

  _continueButtonIdFromDom() {
    const selector = this.element.dataset.railsuiModalInitialFocusSelectorValue;
    if (selector?.startsWith('#')) return selector.slice(1);
    return null;
  }

  _disableContinueButtons(event) {
    this._resolveContinueButtons(event).forEach((button) => {
      button.disabled = true;
    });
  }

  _enableContinueButtons() {
    this.element
      .querySelectorAll('.aquifer-modal__actions button, .aquifer-modal__close, [id$="-error-continue-button"]')
      .forEach((button) => {
        button.disabled = false;
      });
  }
}
